// src/components/world/CharacterAvatar.jsx
// Аватар персонажа. Итерация 4.3–4.4.
// ⚠️ Данные приходят ТОЛЬКО через props (из WorldScene, а тот — из WorldHud).
//    Внутри <Canvas> НЕЛЬЗЯ вызывать useGameSession / useSelector.

import React, { useRef, useEffect, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import './CharacterAvatar.css';

// Безопасное извлечение позиции из characterState.position
function extractPosition(characterState) {
  const p = characterState?.position;
  if (
    p &&
    typeof p === 'object' &&
    typeof p.x === 'number' &&
    typeof p.y === 'number' &&
    typeof p.z === 'number' &&
    Number.isFinite(p.x) &&
    Number.isFinite(p.y) &&
    Number.isFinite(p.z)
  ) {
    return [p.x, p.y, p.z];
  }
  return [0, 0, 0];
}

// Плавный lerp между текущей и целевой позицией
const LERP_FACTOR = 0.1;

export default function CharacterAvatar({ characterState }) {
  const meshRef = useRef();
  const lightRef = useRef();
  const targetPos = useMemo(
    () => new THREE.Vector3(...extractPosition(characterState)),
    [characterState?.position?.x, characterState?.position?.y, characterState?.position?.z]
  );

  // Начальная позиция — сразу в target (без lerp-анимации с 0,0,0 при монтировании)
  useEffect(() => {
    if (meshRef.current && !meshRef.current.userData.initialized) {
      meshRef.current.position.copy(targetPos);
      if (lightRef.current) lightRef.current.position.copy(targetPos);
      meshRef.current.userData.initialized = true;
    }
  }, [targetPos]);

  // Lerp в каждом кадре
  useFrame(() => {
    if (!meshRef.current) return;
    meshRef.current.position.lerp(targetPos, LERP_FACTOR);
    if (lightRef.current) {
      lightRef.current.position.copy(meshRef.current.position);
    }
  });

  const name = characterState?.name || 'Персонаж';

  return (
    <group>
      <mesh ref={meshRef} position={[0, 0, 0]}>
        <sphereGeometry args={[0.3, 24, 24]} />
        <meshStandardMaterial
          color="#00e5ff"
          emissive="#00a0c0"
          emissiveIntensity={0.8}
          metalness={0.6}
          roughness={0.3}
        />
        <Html position={[0, 0.55, 0]} center distanceFactor={10} zIndexRange={[10, 0]}>
          <div className="character-label">{name}</div>
        </Html>
      </mesh>
      {/* Аура (glow) */}
      <pointLight
        ref={lightRef}
        color="#00e5ff"
        intensity={2}
        distance={3}
        decay={2}
      />
    </group>
  );
}
